/**
 * Hands a server-generated .docx (base64) to the user from inside the task pane.
 *
 * A file download is blocked in many Word hosts (see downloadDebugLogFile in
 * RemoteDebugLog.ts), and when it is blocked the click usually does nothing
 * without throwing. So there are two independent ways out — a normal browser
 * download, and opening the file as a new Word document — and callers offer
 * both and point the user at the other one when one fails.
 *
 * Both take their host objects as a parameter so they are unit-tested with
 * fakes; by default they use the real `document` / `Word`.
 */

export const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** How long the blob URL stays alive after the click: revoking it right away
 * cancels the download in some webviews. */
const REVOKE_DELAY_MS = 60000;

export type GeneratedFileResult = { ok: true } | { ok: false; error: string };

type AnchorLike = {
  href: string;
  download: string;
  rel: string;
  style: { display: string };
  click: () => void;
};

export type DownloadEnvironment = {
  createAnchor: () => AnchorLike;
  attach: (anchor: AnchorLike) => void;
  detach: (anchor: AnchorLike) => void;
  createObjectURL: (blob: Blob) => string;
  revokeObjectURL: (url: string) => void;
  defer: (fn: () => void, ms: number) => void;
};

type WordDocumentHost = {
  run: <T>(
    batch: (context: {
      application: {
        createDocument: (base64File?: string) => { open: () => void };
      };
      sync: () => Promise<unknown>;
    }) => Promise<T>,
  ) => Promise<T>;
};

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/** Decodes base64 (a `data:` prefix and whitespace are tolerated). Throws on invalid input. */
export function base64ToBytes(base64: string) {
  const comma = base64.startsWith('data:') ? base64.indexOf(',') : -1;
  const clean = (comma >= 0 ? base64.slice(comma + 1) : base64).replace(/\s+/g, '');
  if (!clean) throw new Error('Empty file content');
  const binary = atob(clean);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function browserDownloadEnvironment(): DownloadEnvironment | null {
  if (typeof document === 'undefined' || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return null;
  }
  return {
    createAnchor: () => document.createElement('a'),
    attach: (anchor) => document.body.appendChild(anchor as HTMLAnchorElement),
    detach: (anchor) => document.body.removeChild(anchor as HTMLAnchorElement),
    createObjectURL: (blob) => URL.createObjectURL(blob),
    revokeObjectURL: (url) => URL.revokeObjectURL(url),
    defer: (fn, ms) => {
      setTimeout(fn, ms);
    },
  };
}

/**
 * Starts a browser download of the file (Blob + `<a download>` click).
 * `ok: true` means the click was dispatched — a host that blocks downloads
 * gives no signal, so the caller should still mention the other way out.
 */
export function downloadBase64File(
  params: { base64: string; filename: string; mimeType?: string },
  env: DownloadEnvironment | null = browserDownloadEnvironment(),
): GeneratedFileResult {
  if (!env) return { ok: false, error: 'Downloads are not available in this host' };
  let url: string | null = null;
  try {
    const bytes = base64ToBytes(params.base64);
    const blob = new Blob([bytes], { type: params.mimeType ?? DOCX_MIME_TYPE });
    url = env.createObjectURL(blob);
    const anchor = env.createAnchor();
    anchor.href = url;
    anchor.download = params.filename;
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    env.attach(anchor);
    try {
      anchor.click();
    } finally {
      env.detach(anchor);
    }
    const createdUrl = url;
    env.defer(() => env.revokeObjectURL(createdUrl), REVOKE_DELAY_MS);
    return { ok: true };
  } catch (err) {
    if (url) env.revokeObjectURL(url);
    return { ok: false, error: errorText(err, 'Download failed') };
  }
}

/**
 * Whether a download started from the task pane reaches the user's disk.
 * Only Word on the web runs the pane in a real browser. A desktop or mobile
 * Word hosts it in an embedded web view that drops an `<a download>` click:
 * nothing throws and nothing is saved (checked in Word for Mac with a real
 * build result — no file appeared in Downloads). There the file goes through
 * the system browser instead, by a one-time link from the server.
 */
export function paneCanDownload(
  platform: unknown = typeof Office !== 'undefined' ? Office.context?.platform : undefined,
): boolean {
  const name = String(platform ?? '');
  return name === '' || name === 'OfficeOnline';
}

function currentWordHost(): WordDocumentHost | null {
  return typeof Word !== 'undefined' && typeof Word.run === 'function' ? (Word as unknown as WordDocumentHost) : null;
}

/**
 * Opens the file as a NEW Word document (WordApi 1.3 `createDocument`). The
 * new document is untitled — Word does not take over the file name.
 */
export async function openBase64DocxInWord(
  base64: string,
  word: WordDocumentHost | null = currentWordHost(),
): Promise<GeneratedFileResult> {
  if (!word) return { ok: false, error: 'Word is not available in this host' };
  try {
    const clean = base64.replace(/\s+/g, '');
    if (!clean) throw new Error('Empty file content');
    await word.run(async (context) => {
      context.application.createDocument(clean).open();
      await context.sync();
    });
    return { ok: true };
  } catch (err) {
    return { ok: false, error: errorText(err, 'Could not open the document in Word') };
  }
}
